import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable } from 'mobx';
import { RootStore } from 'core/stores/root-store';
import { FormControl } from 'shared/libs/form';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { UserApiService } from 'user';
import {
   VerifyAccountCodePayloadDto,
   VerifyAccountCodeResponse,
   VerifyAccountCodeSuccessResponse,
   VerifyAccountUser,
} from './verify-account-dialog.types';

export class VerifyAccountDialogStore {
   readonly confirmState = new AsyncState<VerifyAccountCodeResponse>();
   open = false;
   message = '';
   email = '';
   contact = '';
   link = '';
   expectedUserType: 'candidate' | 'recruiter' = 'candidate';
   readonly codeControl = new FormControl<string>('', [
      (value) => {
         if (!value.trim()) {
            return 'Код обов\'язковий';
         }
         return '';
      },
      (value) => {
         const code = value.trim();
         if (code && !/^\d{5}$/.test(code)) {
            return 'Код має містити 5 цифр';
         }
         return '';
      },
   ]);

   constructor(
      private readonly rootStore: RootStore,
      private readonly language: string
   ) {
      makeObservable(this, {
         open: observable,
         message: observable,
         email: observable,
         contact: observable,
         link: observable,
         expectedUserType: observable,
         processing: computed,
         openDialog: action.bound,
         closeDialog: action.bound,
         setOpen: action.bound,
         reset: action.bound,
      });
   }

   get processing(): boolean {
      return this.confirmState.status === 'pending';
   }

   openDialog({
      message,
      email,
      contact,
      link,
      expectedUserType,
   }: {
      message: string;
      email?: string;
      contact?: string;
      link?: string;
      expectedUserType?: 'candidate' | 'recruiter';
   }): void {
      this.message = message;
      this.email = email || '';
      this.contact = contact || '';
      this.link = link || (this.email ? `mailto:${this.email}` : '');
      this.expectedUserType = expectedUserType || 'candidate';
      this.open = true;
      this.codeControl.reset();
   }

   closeDialog(): void {
      this.open = false;
      this.message = '';
      this.email = '';
      this.contact = '';
      this.link = '';
      this.expectedUserType = 'candidate';
      this.reset();
   }

   setOpen(open: boolean): void {
      if (open) {
         this.open = true;
         return;
      }
      this.closeDialog();
   }

   reset(): void {
      this.codeControl.reset();
   }

   private setUserCookies(user: VerifyAccountUser): void {
      Cookies.set('user_id', String(user.user_id));
      Cookies.set('tokenClient', user.token);
      Cookies.set('user_type', user.user_type);

      if (user.user_type === 'recruiter') {
         Cookies.set('employer_role', user.role ?? '');
         Cookies.set('fname', user.company_name ?? '');
         Cookies.set('employer_id', String(user.employer_id));
         return;
      }

      Cookies.set('lname', user.last_name);
      Cookies.set('fname', user.first_name);
      Cookies.set('contactNumber', user.contact || '');
      Cookies.set('emailAddress', user.email_address);
   }

   private isSuccessResponse(
      response: VerifyAccountCodeResponse
   ): response is VerifyAccountCodeSuccessResponse {
      if (response.status !== 200) {
         return false;
      }

      if (typeof response.response !== 'object' || response.response === null) {
         return false;
      }

      if (Array.isArray(response.response)) {
         return false;
      }

      if (!('user' in response.response)) {
         return false;
      }

      const candidate = response.response as {
         user?: {
            token?: string;
         };
      };

      return typeof candidate.user?.token === 'string' && candidate.user.token.length > 0;
   }

   async confirmCode(
      {
         onSuccess,
         onError,
      }: {
         onSuccess?: (user: VerifyAccountUser) => void | Promise<void>;
         onError?: (message: string) => void | Promise<void>;
      }
   ): Promise<void> {
      this.codeControl.validate();
      if (this.codeControl.invalid) {
         return;
      }

      const payload: VerifyAccountCodePayloadDto = {
         code: Number(this.codeControl.value.trim()),
         email_address: this.email || '',
         contact: this.contact || '',
      };

      await this.confirmState.run(
         () => HttpClient.post<VerifyAccountCodeResponse>('/jobApply/code', payload),
         {
            onSuccess: async (res) => {
               if (this.isSuccessResponse(res)) {
                  const user = res.response.user;

                  if (user.user_type !== this.expectedUserType) {
                     const message = 'Wrong account type';
                     this.codeControl.setError(message);
                     await onError?.(message);
                     return;
                  }

                  await UserApiService.setContext({
                     user_id: user.user_id,
                     user_type: user.user_type,
                     lang: this.language,
                  });

                  this.rootStore.userStore.setUser(user);
                  this.setUserCookies(user);
                  await onSuccess?.(user);
                  return;
               }

               const msg = res.message || 'Invalid verification code';
               this.codeControl.setError(msg);
               await onError?.(msg);
            },
            onError: async (error) => {
               const msg = error.message || 'Failed to verify code';
               this.codeControl.setError(msg);
               await onError?.(msg);
            },
         }
      );
   }
}
